import React, { useEffect } from 'react';
import { useBrandGroupConfig } from '../../hooks/useBrandGroupConfig';
import { useQueryParams } from '../../hooks/useQueryParams';
import { Button, Caption, Paragraph, Stack } from '@mediahuis/chameleon-react';
import { Trans, useTranslation } from 'react-i18next';
import { useTracking } from '../../hooks/useTracking';
import { EventActionValues, EventCategoryValues, EventLabelValues } from '../../types/tracking';
import { BrandUrlsConfig, ENVIRONMENT } from '../../types/general';
import { useSelector } from 'react-redux';
import { apiConfigSelector } from '../../redux/reducers/apiConfigReducer';
import { getEnvironment } from '../../utils/configUtils';

const brandUrls: BrandUrlsConfig = {
  nb: {
    [ENVIRONMENT.DEV]:
      'https://testaboshop.nieuwsblad.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.TST]:
      'https://testaboshop.nieuwsblad.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.UAT]:
      'https://previewaboshop.nieuwsblad.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.PRD]:
      'https://aboshop.nieuwsblad.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
  },
  hbvl: {
    [ENVIRONMENT.DEV]:
      'https://testaboshop.hbvl.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.TST]:
      'https://testaboshop.hbvl.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.UAT]:
      'https://previewaboshop.hbvl.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.PRD]:
      'https://aboshop.hbvl.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
  },
  ds: {
    [ENVIRONMENT.DEV]:
      'https://testaboshop.standaard.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.TST]:
      'https://testaboshop.standaard.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.UAT]:
      'https://previewaboshop.standaard.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.PRD]:
      'https://aboshop.standaard.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
  },
  gva: {
    [ENVIRONMENT.DEV]:
      'https://testaboshop.gva.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.TST]:
      'https://testaboshop.gva.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.UAT]:
      'https://previewaboshop.gva.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
    [ENVIRONMENT.PRD]:
      'https://aboshop.gva.be/actie/opvolgingporeus?utm_campaign=paywall&utm_source=spotifyopenaccess&utm_medium=paywall',
  },
};

function SpotifyUpgrade() {
  const { t } = useTranslation();
  const { trackEventWithCategory } = useTracking();
  const brandGroupConfigHook = useBrandGroupConfig();
  const { brand, cookieId, sessionId, utmInternal } = useQueryParams();
  const brandConfig = brandGroupConfigHook.getBrandConfigFromBrandId(brand);
  const apiConfig = useSelector(apiConfigSelector);
  const currentEnvironment = getEnvironment(apiConfig.fusionAuth.domain as ENVIRONMENT);

  useEffect(() => {
    // On page load and brand/brandGroup present
    if (brand)
      trackEventWithCategory(
        EventActionValues.Show,
        EventLabelValues.SpotifyNoSubscriptionToAboshop,
        EventCategoryValues.AccountLinking,
        EventLabelValues.SpotifyNoSubscriptionToAboshop,
        ''
      );
    // Only depending on brand because otherwise the useEffect would be triggered multiple times
    // and incomplete events would be tracked
  }, [brand]);

  const handleUpgradeLink = () => {
    if (brand) {
      const brandUrlConfig = brandUrls[brand];
      if (brandUrlConfig) {
        const upgradeUrl = brandUrlConfig[currentEnvironment];

        trackEventWithCategory(
          EventActionValues.Click,
          EventLabelValues.SpotifyNoSubscriptionToAboshop,
          EventCategoryValues.AccountLinking,
          EventLabelValues.SpotifyNoSubscriptionToAboshop,
          ''
        );

        const url = new URL(upgradeUrl);
        if (cookieId) url.searchParams.append('utm_cid', cookieId);
        if (sessionId) url.searchParams.append('utm_prsid', sessionId);
        if (utmInternal) url.searchParams.append('utm_internal', utmInternal);

        window.location.href = url.toString();
      }
    }
  };

  return (
    <Stack alignItems="center" flexDirection="column" className="bundling-upgrade px-6 m-auto">
      <Paragraph size="Paragraph" className="bold text-center mt-7">
        <Trans i18nKey={'spotify.upgrade.titleText'}></Trans>
      </Paragraph>
      <Caption size="lg" className="text-center">
        <Trans i18nKey={'spotify.upgrade.description'} values={{ brand: brandConfig?.name }} />
      </Caption>
      <Button
        size="lg"
        onClick={handleUpgradeLink}
        appearance="primary"
        data-cy="upgradeButton"
        className="mb-3">
        {t('spotify.upgrade.buttonText')}
      </Button>
    </Stack>
  );
}

export default SpotifyUpgrade;
